import { ReactComponent as AddIcon } from "assets/images/icons/btn_add.svg";
import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";

import React, { useState, useEffect, useRef } from "react";
import { Box, Button, Typography, TextField } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { cloneDeep } from "lodash";

// Component
import { OutlineRectangleButton } from "components/buttons/OutlineRectangleButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";

const parseMacCsv = (symbol, csvContent) => {
  const lines = csvContent.split(/\r?\n/).map(line => line.trim()).filter(Boolean);
  const formats = {
    colon: /^([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}$/,
    dash: /^([0-9A-Fa-f]{2}-){5}[0-9A-Fa-f]{2}$/,
    plain: /^[0-9A-Fa-f]{12}$/,
  };

  const regex = formats[symbol];
  if (!regex) return [];

  const result = [];

  for (const line of lines) {
    const [macRaw, descRaw = ""] = line.split(",");
    const mac = macRaw.trim();
    const description = descRaw.trim();

    if (regex.test(mac)) {
      result.push({ mac, description });
    }
  }

  return result;
};

const ImportView = ({
  viewStateEnum,
  getViewTitle,
  form,
  macAddressFormat,
  handleImportView,
}) => {
  const theme = useTheme();

  // Status
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);

  // Method
  const closeView = () => {
    handleImportView.closeView();
  }

  const openFileBrowser = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.toLowerCase().endsWith(".csv")) {
      e.target.value = "";
      return;
    }

    setFile(selectedFile);
    e.target.value = "";
  };

  const checkFormValid = () => {
    if (file) {
      return true;
    }

    return false;
  }

  const handleSave = () => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const symbol = macAddressFormat;
      const content = reader.result;
      const validRows = parseMacCsv(symbol, content);
      handleImportView.updateMacAddressItemsList(validRows);
      closeView();
    };

    reader.readAsText(file);
  }

  // Side Effects
  useEffect(() => {

  }, []);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.customFontSize[5],
            fontWeight: theme.typography.customFontWeight[3],
            paddingLeft: "20px",
          }}
        >
          {getViewTitle(viewStateEnum.IMPORT)}
        </Typography>
        <Button
          onClick={closeView}
          sx={{
            backgroundColor: theme.palette.primary.transparent,
            boxShadow: "none",
            "&:hover": {
              backgroundColor: theme.palette.primary.transparent,
            },
          }}
        >
          <CloseIcon />
        </Button>
      </Box>

      <Box
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginBottom: '20px',
        }}
      >
        <Box
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr auto',
            alignItems: 'center',
            width: '80%',
            gap: '20px',
            marginTop: '40px',
          }}
        >
          <Box sx={form.getLabelStyle()}>Choice File:</Box>
          <Box style={{ maxWidth: '100%' }}>
            <TextField
              variant="outlined"
              size="small"
              value={file ? file.name : ""}
              placeholder="Select a file"
              disabled
              sx={{ ...form.getTextfieldStyle(true), width: '100%' }}
            />
            <input
              type="file"
              ref={fileInputRef}
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </Box>
          <OutlineRectangleButton
            startIcon={<AddIcon />}
            height="32px"
            onClick={openFileBrowser}
          >
            Browse
          </OutlineRectangleButton>
        </Box>
      </Box>

      <Box
        sx={{
          margin: "24px 0 16px auto",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <OutlineButton
          onClick={closeView}
          sx={{ marginRight: "20px" }}
          width="100px"
          height="32px"
        >
          Cancel
        </OutlineButton>
        <SolidButton
          onClick={handleSave}
          width="100px"
          height="32px"
          disabled={!checkFormValid()}
        >
          Upload
        </SolidButton>
      </Box>
    </>
  )
}

export default React.memo(ImportView);